import React, { useState, useRef, useEffect } from 'react';
import CustomTooltip from '../../../../components/common/CustomTooltip';
import { dashboardStatusHexColors, getDashboardStatusLabel, getItemTypeColor } from '../../../../constants/integrationTypes';
import Portal from '../../../../components/Portal';

interface PersonBarProps {
  name: string;
  total: number;
  max: number;
  color: string;
  points?: number;
  completedPoints?: number;
  originalEstimate?: number;
  completedOriginalEstimate?: number;
  byStatus?: Record<string, number>;
  byType?: Record<string, number>;
  computedPoints?: number;
  completedComputedPoints?: number;
  computedOriginalEstimate?: number;
  completedComputedOriginalEstimate?: number;
}

const PersonBar: React.FC<PersonBarProps> = ({
  name, total, max, color, points, completedPoints, originalEstimate, completedOriginalEstimate, byStatus, byType,
  computedPoints, completedComputedPoints, computedOriginalEstimate, completedComputedOriginalEstimate
}) => {
  const percentage = max > 0 ? (total / max) * 100 : 0;
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const barRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    // Get the bounding rect of the bar element for left-side positioning
    if (barRef.current) {
      const rect = barRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top + rect.height / 2, // Center vertically relative to the bar
        left: rect.left - 10, // Position to the left of the bar with a small gap
      });
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 200); // 200ms delay to allow moving to tooltip
  };

  return (
    <div
      ref={barRef}
      className="flex flex-col mb-3 group relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Header row with name and metrics */}
      <div className="flex justify-between items-center mb-1.5">
        <div className="truncate text-[10px] text-light-text-secondary dark:text-gray-400 font-medium" title={name}>
          {name}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Combined SP & OE Badge */}
          <div className="flex items-center gap-1.5 text-[9px] bg-gray-50 dark:bg-gray-800/50 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-700">
            <span className={`font-semibold ${(points || 0) > 0 ? 'text-purple-700 dark:text-purple-300' : 'text-gray-400 dark:text-gray-500'}`}>
              {(points || 0).toFixed(1)} <span className={`${(points || 0) > 0 ? 'text-purple-500 dark:text-purple-400' : 'text-gray-400 dark:text-gray-500'}`}>SP</span>
            </span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span className={`font-semibold ${(originalEstimate || 0) > 0 ? 'text-teal-700 dark:text-teal-300' : 'text-gray-400 dark:text-gray-500'}`}>
              {(originalEstimate || 0).toFixed(1)} <span className={`${(originalEstimate || 0) > 0 ? 'text-teal-500 dark:text-teal-400' : 'text-gray-400 dark:text-gray-500'}`}>OE</span>
            </span>
          </div>
          <span className="text-xs font-bold text-light-text-primary dark:text-white">
            {total}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-light-surfaceSecondary dark:bg-slate-700 rounded-full h-6 overflow-hidden relative">
        {/* Stacked bars by status */}
        {byStatus && Object.keys(byStatus).length > 0 ? (
          <div className="h-full flex">
            {Object.entries(byStatus)
              .sort(([, a], [, b]) => (b as number) - (a as number))
              .map(([status, count], idx) => {
                const segmentPercentage = (count / total) * percentage;
                const statusColor = dashboardStatusHexColors[status] || '#9ca3af';
                return (
                  <div
                    key={status}
                    className="h-full transition-all duration-500 ease-out"
                    style={{
                      width: `${segmentPercentage}%`,
                      backgroundColor: statusColor
                    }}
                  />
                );
              })}
          </div>
        ) : (
          <div
            className="h-full rounded-full transition-all duration-500 ease-out"
            style={{ width: `${Math.max(percentage, 8)}%`, backgroundColor: color }}
          />
        )}
      </div>

      {/* Tooltip on hover - rendered in Portal with fixed positioning on the LEFT side */}
      {isHovered && (points !== undefined || originalEstimate !== undefined || byStatus || byType) && (
        <Portal>
          <div
            className="fixed transform -translate-x-full -translate-y-1/2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap z-[9998] pointer-events-auto shadow-xl border border-slate-200 dark:border-slate-700 transition-opacity"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
            onMouseEnter={() => {
              if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
                hoverTimeoutRef.current = null;
              }
            }}
            onMouseLeave={handleMouseLeave}
          >
            <div className="space-y-1">
              <div className="font-bold border-b border-slate-200 dark:border-slate-700 pb-1 mb-1">{name}</div>
              <div className="flex justify-between gap-3">
                <span className="text-slate-500 dark:text-slate-400">Total Work Items:</span>
                <span className="font-semibold">{total}</span>
              </div>

              {/* Story Points with completed */}
              {points !== undefined && (
                <div className="flex justify-between gap-3 items-center">
                  <span className="text-slate-500 dark:text-slate-400">Story Points:</span>
                  <div className="flex items-center gap-2 pointer-events-auto">
                    <span className={`font-semibold ${points > 0 ? 'text-purple-600 dark:text-purple-400' : 'text-slate-400'}`}>
                      {points.toFixed(1)}
                      <CustomTooltip
                        title={(computedPoints || 0) > 0 ? `${(computedPoints || 0).toFixed(1)} SP computed by Cadence Pro` : 'All SP from Azure DevOps'}
                        placement="top"
                        arrow
                      >
                        <span className={`text-[9px] ml-0.5 cursor-help ${(computedPoints || 0) > 0 ? 'text-yellow-500 dark:text-yellow-400' : 'text-slate-300 dark:text-slate-600 opacity-50'}`}>★</span>
                      </CustomTooltip>
                    </span>
                    <span className="text-slate-300 dark:text-slate-600">|</span>
                    <span className={`font-semibold ${(completedPoints || 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-slate-400'}`}>
                      {(completedPoints || 0).toFixed(1)}
                      <CustomTooltip
                        title={(completedComputedPoints || 0) > 0 ? `${(completedComputedPoints || 0).toFixed(1)} Actual SP computed by Cadence Pro` : 'All Actual SP from Azure DevOps'}
                        placement="top"
                        arrow
                      >
                        <span className={`text-[9px] ml-0.5 cursor-help ${(completedComputedPoints || 0) > 0 ? 'text-yellow-500 dark:text-yellow-400' : 'text-slate-300 dark:text-slate-600 opacity-50'}`}>★</span>
                      </CustomTooltip>
                      <span className="text-[10px] text-slate-500 dark:text-slate-400 font-normal ml-1">Completed</span>
                    </span>
                  </div>
                </div>
              )}

              {/* Estimate with completed */}
              {originalEstimate !== undefined && (
                <div className="flex justify-between gap-3 items-center">
                  <span className="text-slate-500 dark:text-slate-400">Original Estimate:</span>
                  <div className="flex items-center gap-2 pointer-events-auto">
                    <span className={`font-semibold ${originalEstimate > 0 ? 'text-teal-600 dark:text-teal-400' : 'text-slate-400'}`}>
                      {originalEstimate.toFixed(1)}h
                      <CustomTooltip
                        title={(computedOriginalEstimate || 0) > 0 ? `${(computedOriginalEstimate || 0).toFixed(1)} hrs computed by Cadence Pro` : 'All Original Estimates from Azure DevOps'}
                        placement="top"
                        arrow
                      >
                        <span className={`text-[9px] ml-0.5 cursor-help ${(computedOriginalEstimate || 0) > 0 ? 'text-yellow-500 dark:text-yellow-400' : 'text-slate-300 dark:text-slate-600 opacity-50'}`}>★</span>
                      </CustomTooltip>
                    </span>
                    <span className="text-slate-300 dark:text-slate-600">|</span>
                    <span className={`font-semibold ${(completedOriginalEstimate || 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-slate-400'}`}>
                      {(completedOriginalEstimate || 0).toFixed(1)} h
                      <CustomTooltip
                        title={(completedComputedOriginalEstimate || 0) > 0 ? `${(completedComputedOriginalEstimate || 0).toFixed(1)} completed hrs computed by Cadence Pro` : 'All completed estimates from Azure DevOps'}
                        placement="top"
                        arrow
                      >
                        <span className={`text-[9px] ml-0.5 cursor-help ${(completedComputedOriginalEstimate || 0) > 0 ? 'text-yellow-500 dark:text-yellow-400' : 'text-slate-300 dark:text-slate-600 opacity-50'}`}>★</span>
                      </CustomTooltip>
                      <span className="text-[10px] text-slate-500 dark:text-slate-400 font-normal ml-1">Completed</span>
                    </span>
                  </div>
                </div>
              )}

              {/* Status breakdown */}
              {byStatus && Object.keys(byStatus).length > 0 && (
                <>
                  <div className="border-t border-slate-200 dark:border-slate-700 pt-1 mt-1">
                    <div className="text-[10px] text-slate-400 dark:text-slate-500 mb-1">By Status:</div>
                  </div>
                  {Object.entries(byStatus)
                    .sort(([, a], [, b]) => (b as number) - (a as number))
                    .map(([status, count]) => {
                      const statusColor = dashboardStatusHexColors[status] || '#9ca3af';
                      const statusLabel = getDashboardStatusLabel(status);
                      return (
                        <div key={status} className="flex justify-between gap-3 items-center">
                          <div className="flex items-center gap-1.5">
                            <div
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: statusColor }}
                            />
                            <span className="text-slate-600 dark:text-slate-300">{statusLabel}:</span>
                          </div>
                          <span className="font-semibold">{count}</span>
                        </div>
                      );
                    })}
                </>
              )}

              {/* Task Type breakdown */}
              {byType && Object.keys(byType).length > 0 && (
                <>
                  <div className="border-t border-slate-200 dark:border-slate-700 pt-1 mt-1">
                    <div className="text-[10px] text-slate-400 dark:text-slate-500 mb-1">By Type:</div>
                  </div>
                  {Object.entries(byType)
                    .sort(([, a], [, b]) => (b as number) - (a as number))
                    .map(([type, count]) => {
                      const lowerType = type.toLowerCase();
                      // Use a default color if getItemTypeColor isn't available or we want to use specific logic
                      const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);
                      return (
                        <div key={type} className="flex justify-between gap-3 items-center">
                          <div className="flex items-center gap-1.5">
                            <span className="text-slate-600 dark:text-slate-300">{typeLabel}:</span>
                          </div>
                          <span className="font-semibold">{count}</span>
                        </div>
                      );
                    })}
                </>
              )}
            </div>
            {/* Arrow pointing to the right (positioned on the right edge of tooltip) */}
            <div className="absolute top-1/2 left-full transform -translate-y-1/2 -ml-px">
              <div className="border-4 border-transparent border-l-white dark:border-l-slate-800"></div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};

export default PersonBar;